/**
 * LibraryScreen — biblioteca de los 1408 ejercicios con GIFs animados.
 *
 * Consume `useCatalog()` que lazy-loadea los ejercicios al primer mount
 * (deduplicado para múltiples consumers paralelos). Al click en un
 * ejercicio se abre un modal con el GIF grande y metadata.
 *
 * Performance: paginación client-side de 60 en 60 (la lista filtrada vive
 * en memoria; no vale la pena infinite-scroll por ahora con sólo 1408).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useCatalog } from "../contexts/CatalogContext";
import { exerciseGifUrl, type Exercise } from "../api/exercises";
import { EmptyState } from "../lib/components";
import { Icon } from "../lib/Icon";
import { normalize } from "../lib/normalizeText";
import { MUSCLE_GROUPS } from "../lib/muscleGroups";

const PAGE = 60;

export function LibraryScreen() {
  const { exercises, loaded, loading, error, ensureLoaded, search } = useCatalog();
  const [q, setQ] = useState("");
  const [muscle, setMuscle] = useState<string>("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [limit, setLimit] = useState(PAGE);
  const [picked, setPicked] = useState<Exercise | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    void ensureLoaded();
  }, [ensureLoaded]);

  // Debounce 200ms del search.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQ(q), 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [q]);

  // Reset paginación al cambiar filtros.
  useEffect(() => {
    setLimit(PAGE);
  }, [debouncedQ, muscle]);

  // Chips usan los grupos canónicos compartidos con el ExercisePickerModal del
  // editor. Antes mostrábamos un chip por cada muscle individual del catálogo
  // ("Glúteo", "Cuádriceps", "Pierna", etc.) y filtrar "Pierna" devolvía 1
  // resultado porque solo matcheaba el muscle exacto.
  // Mantenemos solo los grupos que tienen ejercicios reales en el catálogo
  // (algunos labels pueden ser irrelevantes para el contenido cargado).
  const muscleGroups = useMemo(() => {
    const presentMembers = new Set<string>();
    for (const ex of exercises) {
      const m = ex.muscle || ex.primary_muscle || ex.category;
      if (m) presentMembers.add(normalize(m));
    }
    return MUSCLE_GROUPS.filter((g) => g.members.some((m) => presentMembers.has(m)));
  }, [exercises]);

  const filtered = useMemo(() => {
    if (!loaded) return [];
    // `muscle` ahora es el LABEL del grupo (ej. "Pierna"). Resolvemos al array
    // de submúsculos antes de filtrar — el search() acepta string[] y matchea
    // cualquiera de ellos.
    const group = muscleGroups.find((g) => g.label === muscle);
    const muscleFilter = group ? group.members : muscle;
    const list = search(debouncedQ, muscleFilter);
    // Mostrar primero los que tienen GIF — los sin imagen se ven feos al frente.
    return list.slice().sort((a, b) => {
      const ga = a.video_url || a.gif_url ? 1 : 0;
      const gb = b.video_url || b.gif_url ? 1 : 0;
      return gb - ga;
    });
  }, [loaded, search, debouncedQ, muscle, muscleGroups]);

  const visible = filtered.slice(0, limit);

  return (
    <div className="page al-lib">
      <div className="al-lib-head">
        <div>
          <h1 className="al-page-title">Biblioteca</h1>
          <p className="muted">
            {loaded
              ? `${exercises.length} ejercicios con GIFs animados`
              : "Cargando catálogo…"}
          </p>
        </div>
      </div>

      <div className="al-lib-toolbar">
        <div className="al-lib-search">
          <Icon name="search" size={16} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre, equipo, músculo…"
          />
          {q && (
            <button className="btn btn-ghost btn-sm" onClick={() => setQ("")} title="Limpiar">
              <Icon name="x" size={12} />
            </button>
          )}
        </div>
      </div>

      {muscleGroups.length > 0 && (
        <div className="al-lib-chips">
          <button
            className={"chip " + (muscle === "" ? "primary" : "")}
            onClick={() => setMuscle("")}
          >
            Todos
          </button>
          {muscleGroups.map((g) => (
            <button
              key={g.label}
              className={"chip " + (muscle === g.label ? "primary" : "")}
              onClick={() => setMuscle(g.label === muscle ? "" : g.label)}
            >
              {g.label}
            </button>
          ))}
        </div>
      )}

      {/* ---------- Grid / states ---------- */}
      {error ? (
        <EmptyState
          icon="sparkle"
          title="Algo salió mal"
          body={error}
          action={
            <button className="btn btn-primary" onClick={() => void ensureLoaded()}>
              Reintentar
            </button>
          }
        />
      ) : !loaded && loading ? (
        <div className="al-lib-grid">
          {Array.from({ length: 12 }).map((_, i) => (
            <div className="al-lib-skel" key={i} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="search"
          title="Sin resultados"
          body="Probá otra palabra o cambia el grupo muscular."
        />
      ) : (
        <>
          <div className="al-lib-grid">
            {visible.map((ex) => (
              <button className="al-lib-card" key={ex.id} onClick={() => setPicked(ex)}>
                <div className="al-lib-thumb">
                  {(() => {
                    const url = exerciseGifUrl(ex);
                    // Antes: loading="lazy" — con re-renders rápidos por
                    // filtro, Chrome cancelaba requests y los GIFs quedaban
                    // sin cargar hasta recargar la página. Eager + decoding
                    // async carga todo el lote visible (PAGE=60, manejable).
                    return url ? (
                      <img
                        src={url}
                        alt={ex.name}
                        decoding="async"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="al-lib-thumb-empty">
                        <Icon name="hammer" size={20} />
                      </div>
                    );
                  })()}
                </div>
                <div className="al-lib-meta">
                  <div className="al-lib-name">{ex.name}</div>
                  <div className="tiny muted">
                    {ex.muscle || ex.primary_muscle || ex.category || "—"}
                    {ex.difficulty ? ` · ${ex.difficulty}` : ""}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {visible.length < filtered.length && (
            <div className="al-lib-more">
              <button className="btn btn-secondary" onClick={() => setLimit((l) => l + PAGE)}>
                Cargar más ({filtered.length - visible.length} restantes)
              </button>
            </div>
          )}
        </>
      )}

      {/* ---------- Detalle modal ---------- */}
      {picked && (
        <div className="al-modal-backdrop" onClick={() => setPicked(null)}>
          <div className="al-modal al-lib-detail" onClick={(e) => e.stopPropagation()}>
            <div className="card-header">
              <div className="card-title">{picked.name}</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setPicked(null)}>
                <Icon name="x" size={14} />
              </button>
            </div>

            <div className="al-lib-detail-body">
              <div className="al-lib-detail-thumb">
                {(() => {
                  const url = exerciseGifUrl(picked);
                  return url ? (
                    <img src={url} alt={picked.name} />
                  ) : (
                    <div className="al-lib-thumb-empty">
                      <Icon name="hammer" size={32} />
                    </div>
                  );
                })()}
              </div>
              <div className="al-lib-detail-info">
                <div className="al-lib-detail-row">
                  <span className="stat-label">Grupo muscular</span>
                  <span>{picked.muscle || picked.primary_muscle || picked.category || "—"}</span>
                </div>
                {picked.equipment && (
                  <div className="al-lib-detail-row">
                    <span className="stat-label">Equipo</span>
                    <span>{picked.equipment}</span>
                  </div>
                )}
                {picked.difficulty && (
                  <div className="al-lib-detail-row">
                    <span className="stat-label">Dificultad</span>
                    <span>{picked.difficulty}</span>
                  </div>
                )}
                {picked.secondary_muscles && (
                  <div className="al-lib-detail-row">
                    <span className="stat-label">Músculos secundarios</span>
                    <span>{picked.secondary_muscles}</span>
                  </div>
                )}
                {picked.description && (
                  <div className="al-lib-detail-row">
                    <span className="stat-label">Descripción</span>
                    <span>{picked.description}</span>
                  </div>
                )}
                {picked.instructions && (
                  <div className="al-lib-detail-row">
                    <span className="stat-label">Ejecución</span>
                    <span style={{ whiteSpace: "pre-line" }}>{picked.instructions}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
